import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { generateUUID } from '@renderer/utils/idUtils'
import cogoToast from '@successtar/cogo-toast'
import Button from '@renderer/components/bahela/Button'
import PromoModal from '@renderer/components/bahela/PromoModal'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'
import { useShouldShowLanguageSelector } from '@renderer/hooks'

// Import Bahela assets
import bahelaLogo from '/assets/bahela/bahela.webp'
import bgLayer from '/assets/bahela/bg-layer.webp'
import ornament from '/assets/bahela/ornament-welcome.webp'
import questionIcon from '/assets/bahela/question-icon.webp'
import { RecordingType } from '@renderer/types/configState'

const Welcome: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [showPromoModal, setShowPromoModal] = useState(false)
  const [discount, setDiscount] = useState(0)
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, uploadSessionState, logActivity, transaction } = useSessionStateStore()
  const { config, getPageSettings } = useConfigStore()
  const welcomeSettings = getPageSettings('welcome')
  const [isSessionStarting, setIsSessionStarting] = useState(false)
  // EN/ID toggle is only meaningful in IDR projects; for any other
  // currency we force English (see `useEnforceProjectLanguage` in
  // ProjectRoute) and the picker is hidden so customers don't get
  // confused by an option that does nothing for their locale.
  const showLanguageToggle = useShouldShowLanguageSelector()

  // Sync local discount state with session state on component mount
  useEffect(() => {
    if (transaction.discount && transaction.discount !== discount) {
      setDiscount(transaction.discount)
    }
  }, [transaction.discount])

  const startSession = async (): Promise<void> => {
    if (isSessionStarting) {
      return // Prevent multiple calls
    }
    setIsSessionStarting(true)

    try {
      // Credit deduction is now handled in Capture.tsx when capture session starts
      // This ensures customers only pay when they actually start capturing

      const sessionId = generateUUID()

      const recordingType = config?.pages?.checkout?.settings?.packages?.find(
        (pkg) =>
          pkg.type === 'recording' ||
          pkg.type === 'strip_live' ||
          pkg.type === 'strip_story' ||
          pkg.type === 'session_recording'
      )?.type as RecordingType

      const prev = useSessionStateStore.getState()
      setSessionState({
        id: sessionId,
        projectId,
        mode: 'capture',
        timeline: {
          startTime: new Date()
        },
        userPreferences: {
          ...prev.userPreferences,
          useGIF: getPageSettings('capture')?.gifEnabled,
          useRecording: getPageSettings('capture')?.recordingEnabled,
          useVideo: getPageSettings('record')?.enabled,
          recordingType
        },
        gallery: {
          id: `${9999999999999 - Date.now()}-${sessionId}`
        },
        // Start every session with a clean transaction. `setSessionState` MERGES the
        // transaction sub-object, so a previous session that ended without cleanupSession
        // (e.g. camera-recovery nav-home, back/restart buttons) would otherwise leak its
        // orderId / settlement status into this brand-new session — letting the next
        // customer skip payment or corrupting revenue attribution. Reset the payment-identity
        // fields explicitly; preserve only the applied voucher discount.
        transaction: {
          orderId: null,
          qrUrl: null,
          expiryTime: null,
          serverTime: null,
          status: 'pending',
          amount: 0,
          extraPrintPaymentStatus: undefined,
          selectedPackages: [],
          printed: false,
          extraPrintAmount: 0,
          extraPrintCopies: 0,
          discount: discount
        }
      })

      // Log session start activity
      logActivity('Session started', {
        mode: 'capture',
        discount: discount
      })

      const nextPage = welcomeSettings?.startWithPayment ? 'checkout' : 'filter'
      uploadSessionState()
      navigate(`/${projectId}/${nextPage}`)
    } catch (error) {
      console.error('Error starting session:', error)
      cogoToast.error(t('bahela.welcome.startError'))
    } finally {
      setIsSessionStarting(false)
    }
  }

  const handlePromoApplied = (discount: number): void => {
    setDiscount(discount)
    const prevTx = useSessionStateStore.getState().transaction
    setSessionState({
      transaction: {
        ...prevTx,
        discount
      }
    })

    // Log promo application activity
    logActivity('Promo code applied', {
      discount,
      isFixedAmount: discount > 0 // If positive, it's fixed amount; if negative, it's percentage
    })
  }

  const changeLanguage = (lng: string): void => {
    i18n.changeLanguage(lng)
    localStorage.setItem('language', lng)
  }

  // Call all hooks at the top level
  const cachedOrnament = useCachedBackgroundImage(ornament)
  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedQuestionIcon = useCachedImage(questionIcon)
  const cachedBahelaLogo = useCachedImage(bahelaLogo)

  return (
    <div className="background relative bg-hibiscus w-full h-full overflow-hidden">
      <div
        className="deco-img absolute bottom-0 left-0 w-full h-[143px] bg-cover z-[0]"
        style={{ backgroundImage: `url(${cachedOrnament})` }}
      />
      <div
        className="deco-img absolute top-0 left-0 w-full h-full bg-cover z-[1]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      />
      <Button
        onClick={() => navigate(`/${projectId}/tutorial`)}
        className="absolute text-[43px] left-20 bottom-[190px] flex gap-4 z-[2]"
      >
        {t('bahela.welcome.tutorial')}
        <img alt="question-icon" src={cachedQuestionIcon} className="w-[39px] h-[39px]" />
      </Button>
      <img
        src={cachedBahelaLogo}
        alt="Bahela"
        className="z-[3] object-contain w-32 h-32 absolute top-20 right-20"
      />
      {showLanguageToggle && (
        <div className="z-[4] border-[7px] inline-flex drop-shadow-xl items-center border-uriYellow rounded-full text-[36px] p-1 font-sans font-bold absolute top-24 left-20">
          <button
            id="lang-id"
            className={`px-4 py-2 rounded-full w-[77px] h-[56px] flex justify-center items-center ${
              i18n.language === 'id' ? 'bg-uriYellow text-hibiscus' : 'text-uriYellow'
            }`}
            onClick={() => changeLanguage('id')}
          >
            ID
          </button>
          <button
            type="button"
            id="lang-en"
            className={`px-4 py-2 rounded-full w-[77px] h-[56px] flex justify-center items-center ${
              i18n.language === 'en' ? 'bg-uriYellow text-hibiscus' : 'text-uriYellow'
            }`}
            onClick={() => changeLanguage('en')}
          >
            EN
          </button>
        </div>
      )}
      <div className="z-[5] absolute top-[275px] flex flex-col gap-4 justify-center items-center text-center w-full">
        <h1 className="z-0 flex gap-0 items-center w-full text-center leading-none justify-center">
          <span className="relative flex flex-col gap-0 text-uriYellow">
            <span
              className={`font-deco-bahela z-20 ${i18n.language === 'id' ? 'text-[124px]' : 'text-[163px]'}`}
            >
              {t('bahela.welcome.title')}
            </span>
            <span
              className={`font-serif-bahela z-0 ${i18n.language === 'id' ? 'text-[156px]' : 'text-[130px]'}`}
            >
              {t('bahela.welcome.subtitle')}
            </span>
            <span className="absolute bottom-1.5 -left-1.5 flex flex-col gap-0 text-white">
              <span
                className={`font-deco-bahela z-30 ${i18n.language === 'id' ? 'text-[124px]' : 'text-[163px]'}`}
              >
                {t('bahela.welcome.title')}
              </span>
              <span
                className={`font-serif-bahela z-10 ${i18n.language === 'id' ? 'text-[156px]' : 'text-[130px]'}`}
              >
                {t('bahela.welcome.subtitle')}
              </span>
            </span>
          </span>
        </h1>
        <Button className="text-[90px] px-20 grow-shrink" onClick={startSession}>
          {t('bahela.welcome.startButton')}
        </Button>
      </div>
      <Button
        className={`${discount === 0 ? 'opacity-100' : 'opacity-30'} absolute text-[43px] right-20 bottom-[190px] flex gap-4 z-[2]`}
        textColor="hibiscus"
        onClick={() => {
          if (discount === 0) {
            setShowPromoModal(true)
          } else {
            const projectCurrency = normalizeCurrency(config?.resolvedCurrency)
            const message =
              discount > 0
                ? t('bahela.welcome.fixedDiscountApplied', {
                    value: formatAmount(discount, projectCurrency)
                  })
                : t('bahela.welcome.percentageDiscountApplied', { value: Math.abs(discount) })
            cogoToast.info(message)
          }
        }}
      >
        {discount === 0 ? t('bahela.welcome.promoButton') : `${t('bahela.welcome.promoButton')} ✓`}
      </Button>

      {showPromoModal && (
        <PromoModal
          showModal={showPromoModal}
          setShowModal={setShowPromoModal}
          onPromoApplied={handlePromoApplied}
        />
      )}
    </div>
  )
}

export default Welcome
